
// wiringEvaluator.js

const TERMINAL_MAPPING = {
  "W or W1": "W",
  "Y or Y1": "Y",
  "O/B": "OB",
  "G or F": "G",
  "S1 or S2 or S3": "S",
};

const normalizeTerminals = (terminals = []) =>
  terminals.map(
    terminal => TERMINAL_MAPPING[terminal] || terminal
  );

const createResult = (
  screen,
  name,
  badge,
  systemType = "—",
  reason,
  wiringMap = []
) => ({
  screen,
  name,
  badge,
  systemType,
  reason,
  wiringMap,
});

const buildWiringMap = (selectedTerminals, rWiring) => {
  const terminals = new Set(normalizeTerminals(selectedTerminals));

  const map = [];

  const has = wire => terminals.has(wire);

  const connect = (old, nw) =>
    map.push({ old, nw, action: "connect" });

  const tapeOff = old =>
    map.push({ old, nw: "—", action: "tape-off" });

  const hasW = has("W");
  const hasW2 = has("W2");
  const hasW3 = has("W3");
  const hasAux = has("Aux");
  const hasY = has("Y");
  const hasZ = has("Z");

  // Power
  if (rWiring === "DUAL_R") {
    if (has("Rh") && has("Rc")) {
      connect("Rh", "RH");
      connect("Rc", "RC");
    } else if (has("R") && has("Rh")) {
      connect("Rh", "RH");
      connect("R", "RC");
    } else if (has("R") && has("Rc")) {
      connect("R", "RH");
      connect("Rc", "RC");
    }
  } else {
    const powerWire = has("Rh")
      ? "Rh"
      : has("Rc")
      ? "Rc"
      : "R";

    connect(powerWire, "RH");
  }

  // Common
  ["C", "COM", "X"].forEach(wire => {
    if (has(wire)) connect(wire, "C");
  });

  // Heating
  if (hasW) connect("W", "W");

  if (hasW2) {
    connect("W2", !hasW && !hasAux ? "W" : "W2");
  }

  if (hasW3) {
    if (!hasW && !hasW2) {
      connect("W3", "W");
    } else if (hasW && !hasW2) {
      connect("W3", "W2");
    } else {
      connect("W3", "Z1");
    }
  }

  if (hasAux) {
    if (hasW && hasW2) {
      tapeOff("Aux");
    } else if (hasW) {
      connect("Aux", "W2");
    } else {
      connect("Aux", "W");
    }
  }

  if (has("E")) {
    const hasExistingHeat =
      hasW || hasW2 || hasAux;

    if (hasExistingHeat) {
      tapeOff("E");
    } else {
      connect("E", "W");
    }
  }

  // Cooling
  if (hasY) connect("Y", "Y");

  if (has("Y2")) {
    connect("Y2", hasY ? "Y2" : "Y");
  }

  // Fan
  if (has("G")) connect("G", "G");

  // Heat Pump
  if (has("O")) connect("O", "O");
  if (has("B")) connect("B", "B");
  if (has("OB")) connect("O/B", "O");

  // Accessories
  const z1Occupied = hasW3 && hasW && hasW2;

  if (hasZ) {
    connect("Z", z1Occupied ? "Z2" : "Z1");
  }

  if (has("dh")) {
    connect(
      "dh",
      z1Occupied || hasZ ? "Z2" : "Z1"
    );
  }

  // Tape-offs
  ["S", "L", "T", "GND", "ODT"].forEach(wire => {
    if (has(wire)) tapeOff(wire);
  });

  return map;
};

export const evaluate = (
  selectedTerminals = []
) => {
  const terminals = new Set(
    normalizeTerminals(selectedTerminals)
  );

  const has = wire => terminals.has(wire);

  const hasR =
    has("R") || has("Rh") || has("Rc");

  const hasHP =
    has("O") || has("B") || has("OB");

  const hasAux = has("Aux");
  const hasE = has("E");
  const hasW3 = has("W3");

  const hasCooling =
    has("Y") || has("Y2");

  const hasG = has("G");

  // P1
  if (has("Other")) {
    return createResult(
      "S5",
      "Unrecognized Terminal",
      "red",
      "—",
      'P1: "Other" terminal selected'
    );
  }

  // P2
  if (has("K")) {
    return createResult(
      "S4",
      "K Terminal — Rewiring required",
      "red",
      "—",
      "P2: K terminal selected"
    );
  }

  // P3
  if (!hasR) {
    return createResult(
      "S3",
      "Uncommon Wiring",
      "gray",
      "—",
      "P3: No power wire (R/Rh/Rc)"
    );
  }

  const rCount = ["R", "Rh", "Rc"].filter(has).length;

  if (rCount === 3) {
    return createResult(
      "S3",
      "Uncommon Wiring",
      "gray",
      "—",
      "V1: R + Rh + Rc all selected"
    );
  }

  const isDualR =
    (has("Rh") && has("Rc")) ||
    (has("R") && has("Rh")) ||
    (has("R") && has("Rc"));

  const isStrictDualR =
    has("Rh") && has("Rc") && !has("R");

  const cCount = ["C", "COM", "X"].filter(has).length;

  if (cCount >= 2) {
    return createResult(
      "S3",
      "Uncommon Wiring",
      "gray",
      "—",
      "V2: Multiple C-wire aliases selected"
    );
  }

  if ((hasAux || hasE) && !hasHP) {
    return createResult(
      "S3",
      "Uncommon Wiring",
      "gray",
      "—",
      "V3: Aux/E without HP wire"
    );
  }

  if (hasHP) {
    const stages =
      (has("W") ? 1 : 0) +
      (has("W2") ? 1 : 0) +
      (hasW3 ? 1 : 0) +
      (hasAux ? 1 : 0) +
      (hasE ? 1 : 0);

    if (stages > 3) {
      return createResult(
        "S3",
        "Uncommon Wiring",
        "gray",
        "—",
        `V4: HP heat stages > 3 (${stages})`
      );
    }
  }

  if (hasCooling && !hasG) {
    return createResult(
      "S3",
      "Uncommon Wiring",
      "gray",
      "—",
      "V5: Y/Y2 without G"
    );
  }

  if (hasHP && !hasCooling) {
    return createResult(
      "S3",
      "Uncommon Wiring",
      "gray",
      "—",
      "V6: HP wire without Y/Y2"
    );
  }

  if (has("O") && has("B")) {
    return createResult(
      "S3",
      "Uncommon Wiring",
      "gray",
      "—",
      "V7: Both O and B selected"
    );
  }

  if (isStrictDualR && !hasHP) {
    const hasHeatWire =
      has("W") ||
      has("W2") ||
      has("W3") ||
      hasAux ||
      hasE;

    if (!hasHeatWire || !hasCooling) {
      return createResult(
        "S3",
        "Uncommon Wiring",
        "gray",
        "—",
        "V8: Dual R without both heat+cool"
      );
    }
  }

  if (
    hasW3 &&
    has("W") &&
    has("W2") &&
    has("Z") &&
    has("dh")
  ) {
    return createResult(
      "S3",
      "Uncommon Wiring",
      "gray",
      "—",
      "V9: W3+Z+dh slot conflict"
    );
  }

  if (
    has("OB") &&
    (has("O") || has("B"))
  ) {
    return createResult(
      "S3",
      "Uncommon Wiring",
      "gray",
      "—",
      "V10: O/B conflict"
    );
  }

  const hasHeat =
    has("W") ||
    has("W2") ||
    has("W3") ||
    hasAux ||
    hasE;

  let systemType;

  if (hasHP && hasHeat) {
    systemType = "Heat Pump + Aux Heat";
  } else if (hasHP) {
    systemType = "Heat Pump (no aux)";
  } else if (hasCooling && hasHeat) {
    systemType = "Conventional";
  } else if (hasCooling) {
    systemType = "Cool Only";
  } else if (hasHeat) {
    systemType = "Heat Only";
  } else if (hasG) {
    systemType = "Fan Only";
  } else {
    return createResult(
      "S3",
      "Uncommon Wiring",
      "gray",
      "—",
      "System type unrecognized"
    );
  }

  const hasC =
    has("C") ||
    has("COM") ||
    has("X");

  const rWiring = isDualR
    ? "DUAL_R"
    : "SINGLE_R";

  const wiringMap = buildWiringMap(
    selectedTerminals,
    rWiring
  );

  return hasC
    ? createResult(
        "CONNECT_WIRES",
        "Connect your wires (Wiring Map)",
        "green",
        systemType,
        "Recognized system + C wire",
        wiringMap
      )
    : createResult(
        "POWER_PLATE",
        "Power Plate required",
        "yellow",
        systemType,
        "Recognized system + no C wire"
      );
};
